import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { MdEdit } from 'react-icons/md';
import { SearchableSelect } from './SearchableSelect';
import { CATEGORY_NAME_MAX_LEN, validateSimpleName } from '../../utils/validation';
import {
  useCreateProductCategoryMutation,
  useGetProductCategoriesQuery,
  useUpdateProductCategoryMutation,
  useUpdateProductCategoryStatusMutation,
} from '../../redux/productCategoryApi';
import { useAuth } from '../../context/AuthContext';
import TablePagination from './TablePagination';
import { ApiLoadTableRow } from './ApiLoadState';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';

const STATUS_FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

function displayValue(value) {
  return value === '' || value == null ? '—' : String(value);
}

function AdminProductCategories() {
  const { canAction } = useAuth();
  const canCreate = canAction('product_category', 'create');
  const canEdit = canAction('product_category', 'edit');
  const canDelete = canAction('product_category', 'delete');
  const hasActions = canEdit || canDelete;
  const tableColSpan = hasActions ? 6 : 5;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newBuffer, setNewBuffer] = useState('');
  const [newGuestCount, setNewGuestCount] = useState('');
  const [newLeadTime, setNewLeadTime] = useState('');
  const [error, setError] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editBuffer, setEditBuffer] = useState('');
  const [editGuestCount, setEditGuestCount] = useState('');
  const [editLeadTime, setEditLeadTime] = useState('');
  const [editError, setEditError] = useState('');
  const pageSize = DEFAULT_PAGE_SIZE;

  const listQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    if (filter === 'active') params.status = true;
    else if (filter === 'inactive') params.status = false;
    return params;
  }, [page, pageSize, search, filter]);

  const { data: listRes, isFetching, isLoading, error: fetchError, refetch } =
    useGetProductCategoriesQuery(listQuery);
  const [createProductCategory, { isLoading: isCreating }] =
    useCreateProductCategoryMutation();
  const [updateProductCategory, { isLoading: isUpdating }] =
    useUpdateProductCategoryMutation();
  const [updateProductCategoryStatus, { isLoading: isUpdatingStatus }] =
    useUpdateProductCategoryStatusMutation();

  const rows = useMemo(
    () =>
      (listRes?.data || []).map((item) => ({
        id: item.id,
        name: item.name || '',
        active: item.status === true,
        buffer: item.buffer ?? '',
        guestCount: item.guest_count ?? item.guestCount ?? '',
        leadTime: item.lead_time ?? item.leadTime ?? '',
      })),
    [listRes]
  );

  const { currentPage, totalCount, pageSize: effectivePageSize } = getServerPagination(
    listRes,
    { page, pageSize }
  );

  const openAdd = () => {
    setIsAddOpen(true);
    setNewName('');
    setNewBuffer('');
    setNewGuestCount('');
    setNewLeadTime('');
    setError('');
  };

  const closeAdd = () => setIsAddOpen(false);

  const openEdit = (row) => {
    setIsEditOpen(true);
    setEditId(row.id);
    setEditName(row.name || '');
    setEditBuffer(row.buffer === '' || row.buffer == null ? '' : String(row.buffer));
    setEditGuestCount(
      row.guestCount === '' || row.guestCount == null ? '' : String(row.guestCount)
    );
    setEditLeadTime(row.leadTime === '' || row.leadTime == null ? '' : String(row.leadTime));
    setEditError('');
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditId(null);
    setEditName('');
    setEditBuffer('');
    setEditGuestCount('');
    setEditLeadTime('');
    setEditError('');
  };

  const toggleActive = (row) => {
    if (isUpdatingStatus) return;
    updateProductCategoryStatus({
      id: Number(row.id),
      name: row.name,
      status: !row.active,
    })
      .unwrap()
      .then(() => toast.success('Product category status updated successfully.'))
      .catch((err) =>
        toast.error(err?.data?.message || 'Failed to update product category status.')
      );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = newName.trim();
    const validationError = validateSimpleName(trimmed, {
      fieldLabel: 'Product category name',
    });
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }
    const exists = rows.some((r) => r.name.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      const msg = 'This product category already exists';
      setError(msg);
      toast.error(msg);
      return;
    }
    try {
      await createProductCategory({
        name: trimmed,
        status: true,
        buffer: String(newBuffer ?? '').trim(),
        guest_count: String(newGuestCount ?? '').trim(),
        lead_time: String(newLeadTime ?? '').trim(),
      }).unwrap();
      toast.success('Product category created successfully.');
      closeAdd();
      setNewName('');
      setNewBuffer('');
      setNewGuestCount('');
      setNewLeadTime('');
    } catch (err) {
      const msg = err?.data?.message || 'Failed to create product category.';
      setError(msg);
      toast.error(msg);
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    const trimmed = editName.trim();
    const validationError = validateSimpleName(trimmed, {
      fieldLabel: 'Product category name',
    });
    if (validationError) {
      setEditError(validationError);
      toast.error(validationError);
      return;
    }
    const exists = rows.some(
      (r) =>
        Number(r.id) !== Number(editId) &&
        r.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      const msg = 'This product category already exists';
      setEditError(msg);
      toast.error(msg);
      return;
    }
    const current = rows.find((r) => Number(r.id) === Number(editId));
    try {
      await updateProductCategory({
        id: Number(editId),
        name: trimmed,
        status: current ? current.active : true,
        buffer: String(editBuffer ?? '').trim() === '' ? undefined : Number(editBuffer),
        guest_count:
          String(editGuestCount ?? '').trim() === '' ? undefined : Number(editGuestCount),
        lead_time: String(editLeadTime ?? '').trim() === '' ? undefined : Number(editLeadTime),
      }).unwrap();
      toast.success('Product category updated successfully.');
      closeEdit();
    } catch (err) {
      const msg = err?.data?.message || 'Failed to update product category.';
      setEditError(msg);
      toast.error(msg);
    }
  };

  return (
    <DashboardLayout title="Product Category">
      <div className="space-y-4 py-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex w-full flex-col gap-3 md:max-w-2xl md:flex-row md:items-center">
              <div className="relative w-full min-w-0 md:max-w-xs">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search Product Category"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>
              <label className="flex w-full min-w-0 flex-col gap-1.5 text-sm text-gray-700 md:w-56 md:shrink-0 md:flex-row md:items-center md:gap-2">
                <span className="shrink-0 font-medium">Filter By:</span>
                <div className="w-full min-w-0">
                  <SearchableSelect
                    name="filter"
                    value={filter}
                    onChange={(e) => {
                      setFilter(e.target.value || 'all');
                      setPage(1);
                    }}
                    placeholder="All"
                    clearable={false}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    options={STATUS_FILTER_OPTIONS}
                  />
                </div>
              </label>
            </div>
            {canCreate ? (
              <button
                type="button"
                className="inline-flex w-full shrink-0 items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm md:w-auto"
                onClick={openAdd}
              >
                + Add Product Category
              </button>
            ) : null}
          </div>

          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Sr. No.
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Product Category Name
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Buffer
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Guest Count
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Lead Time
                  </th>
                  {hasActions ? (
                    <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold text-gray-700">
                      Action
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                <ApiLoadTableRow
                  colSpan={tableColSpan}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  hasData={rows.length > 0}
                  error={fetchError}
                  refetch={refetch}
                  message="Loading product categories..."
                />
                {!fetchError &&
                  (rows.length > 0 || (!isLoading && !isFetching)) &&
                  rows.map((row, index) => (
                    <tr key={row.id} className="hover:bg-amber-50/40">
                      <td className="border-b border-gray-100 px-3 py-2">
                        {(currentPage - 1) * effectivePageSize + index + 1}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2">{row.name}</td>
                      <td className="border-b border-gray-100 px-3 py-2">
                        {displayValue(row.buffer)}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2">
                        {displayValue(row.guestCount)}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2">
                        {displayValue(row.leadTime)}
                      </td>
                      {hasActions ? (
                        <td className="border-b border-gray-100 px-3 py-2 text-right">
                          <div className="inline-flex items-center justify-end gap-3">
                            {canEdit ? (
                              <button
                                type="button"
                                disabled={isUpdating}
                                onClick={() => openEdit(row)}
                                className="rounded-md p-1 text-red-700 hover:bg-amber-50/60 hover:text-red-800 disabled:opacity-50"
                                aria-label="Edit product category"
                                title="Edit"
                              >
                                <MdEdit className="h-5 w-5 text-red-700" />
                              </button>
                            ) : null}
                            {canDelete ? (
                              <button
                                type="button"
                                disabled={isUpdatingStatus}
                                onClick={() => toggleActive(row)}
                                aria-label={
                                  row.active
                                    ? 'Disable product category'
                                    : 'Enable product category'
                                }
                                className={`inline-flex h-5 w-10 items-center rounded-full border px-[2px] transition disabled:opacity-50 ${
                                  row.active
                                    ? 'border-emerald-600 bg-[#008E36]'
                                    : 'border-gray-300 bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                    row.active ? 'translate-x-5' : 'translate-x-0'
                                  }`}
                                />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                {!fetchError && !isLoading && !isFetching && rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={tableColSpan}
                      className="px-3 py-4 text-center text-sm text-gray-500"
                    >
                      No product categories found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <TablePagination
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
            pageSize={effectivePageSize}
          />
        </div>

        {isAddOpen ? (
          <div className="fixed inset-0 z-30 flex justify-end bg-black/30">
            <div className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white p-4 shadow-2xl sm:p-6">
              <div className="mb-4 flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 pb-4">
                <h2 className="text-lg font-semibold text-brand">Add Product Category</h2>
                <button
                  type="button"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                  onClick={closeAdd}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSave} className="flex flex-col gap-4">
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>
                    Product Category Name <span className="text-red-600">*</span>
                  </span>
                  <input
                    type="text"
                    placeholder="Enter Product Category Name"
                    value={newName}
                    maxLength={CATEGORY_NAME_MAX_LEN}
                    onChange={(e) => {
                      setNewName(e.target.value);
                      setError('');
                    }}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>Buffer</span>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="Enter buffer"
                    value={newBuffer}
                    onChange={(e) => setNewBuffer(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>Guest Count</span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="Enter guest count"
                    value={newGuestCount}
                    onChange={(e) => setNewGuestCount(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>Lead Time (Days)</span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="Enter lead time"
                    value={newLeadTime}
                    onChange={(e) => setNewLeadTime(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                {error ? <p className="text-sm text-red-600">{error}</p> : null}
                <button
                  type="submit"
                  disabled={isCreating}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:opacity-50 sm:w-[140px]"
                >
                  {isCreating ? 'Saving...' : 'Save'}
                </button>
              </form>
            </div>
          </div>
        ) : null}

        {isEditOpen ? (
          <div className="fixed inset-0 z-30 flex justify-end bg-black/30">
            <div className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white p-4 shadow-2xl sm:p-6">
              <div className="mb-4 flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 pb-4">
                <h2 className="text-lg font-semibold text-brand">Edit Product Category</h2>
                <button
                  type="button"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                  onClick={closeEdit}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleEditSave} className="flex flex-col gap-4">
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>
                    Product Category Name <span className="text-red-600">*</span>
                  </span>
                  <input
                    type="text"
                    placeholder="Enter Product Category Name"
                    value={editName}
                    maxLength={CATEGORY_NAME_MAX_LEN}
                    onChange={(e) => {
                      setEditName(e.target.value);
                      setEditError('');
                    }}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>Buffer</span>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="Enter buffer"
                    value={editBuffer}
                    onChange={(e) => setEditBuffer(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>Guest Count</span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="Enter guest count"
                    value={editGuestCount}
                    onChange={(e) => setEditGuestCount(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>Lead Time (Days)</span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="Enter lead time"
                    value={editLeadTime}
                    onChange={(e) => setEditLeadTime(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                {editError ? <p className="text-sm text-red-600">{editError}</p> : null}
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:opacity-50 sm:w-[140px]"
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </button>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
}

export default AdminProductCategories;
